<template>
  <AppLayout 
    page-title="Autonomous AI Agents" 
    page-subtitle="Fully autonomous AI agents powered by Youtu-agent framework"
  >
    <div class="space-y-8">

      <!-- Quick Start -->
      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <div class="flex items-center mb-4">
          <div class="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center mr-3">
            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
          </div>
          <h2 class="text-xl font-semibold text-white">Ready to Use AI Agents</h2>
        </div>
        <p class="text-slate-300 mb-4">Create autonomous AI agents that can handle complex oilfield tasks. No setup required - just define your goal and let the AI work!</p>
        
        <!-- Example Goals -->
        <div class="bg-slate-700/30 rounded-lg p-4 border border-slate-600/50 mb-4">
          <h3 class="font-medium text-white mb-3">💡 Example Goals You Can Try:</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div class="flex items-start">
              <span class="text-emerald-400 mr-2">🔒</span>
              <span class="text-slate-300">"Conduct a safety audit of all active job sites"</span>
            </div>
            <div class="flex items-start">
              <span class="text-green-400 mr-2">💰</span>
              <span class="text-slate-300">"Generate invoices for all completed timesheets this month"</span>
            </div>
            <div class="flex items-start">
              <span class="text-purple-400 mr-2">📅</span>
              <span class="text-slate-300">"Optimize crew scheduling for the next 2 weeks"</span>
            </div>
            <div class="flex items-start">
              <span class="text-orange-400 mr-2">📋</span>
              <span class="text-slate-300">"Ensure all regulatory documentation is up to date"</span>
            </div>
          </div>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="bg-slate-700/30 rounded-lg p-4 border border-slate-600/50">
            <h3 class="font-medium text-white mb-2">🎯 Goal-Driven</h3>
            <p class="text-sm text-slate-300">Simply describe what you want accomplished</p>
          </div>
          <div class="bg-slate-700/30 rounded-lg p-4 border border-slate-600/50">
            <h3 class="font-medium text-white mb-2">🤖 Autonomous</h3>
            <p class="text-sm text-slate-300">AI agents work independently without supervision</p>
          </div>
          <div class="bg-slate-700/30 rounded-lg p-4 border border-slate-600/50">
            <h3 class="font-medium text-white mb-2">⚡ Real-Time</h3>
            <p class="text-sm text-slate-300">Watch agents execute tasks step-by-step</p>
          </div>
        </div>
        
        <!-- Quick Demo Button -->
        <div class="mt-4 text-center">
          <button 
            @click="quickDemo"
            class="px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white rounded-lg hover:from-emerald-600 hover:to-cyan-600 transition-all duration-200 font-medium"
          >
            🚀 Try Quick Demo
          </button>
        </div>
      </div>

      <!-- Agent Creation -->
      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <h2 class="text-xl font-semibold text-white mb-4">Create New Agent</h2>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Agent Type Selection -->
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Agent Type</label>
            <div class="space-y-3">
              <div 
                v-for="agentType in Object.values(OILFIELD_AGENTS)" 
                :key="agentType.id"
                @click="selectAgentType(agentType)"
                :class="[
                  'border rounded-lg p-4 cursor-pointer transition-colors',
                  selectedAgentType?.id === agentType.id 
                    ? 'border-emerald-500 bg-emerald-500/10' 
                    : 'border-slate-600 hover:border-slate-500 bg-slate-700/30'
                ]"
              >
                <h3 class="font-medium text-white">{{ agentType.name }}</h3>
                <p class="text-sm text-slate-300 mt-1">{{ agentType.description }}</p>
                <div class="flex flex-wrap gap-1 mt-2">
                  <span 
                    v-for="capability in agentType.capabilities.slice(0, 3)" 
                    :key="capability"
                    class="px-2 py-1 bg-slate-600/50 text-xs text-slate-300 rounded"
                  >
                    {{ capability }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Agent Configuration -->
          <div v-if="selectedAgentType">
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Goal</label>
                <textarea 
                  v-model="newAgentGoal" 
                  rows="4"
                  class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Describe what this agent should accomplish..."
                ></textarea>
              </div>

              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Context (Optional)</label>
                <textarea 
                  v-model="newAgentContext" 
                  rows="3"
                  class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Additional context or constraints..."
                ></textarea>
              </div>

              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">AI Model</label>
                <select 
                  v-model="selectedModel"
                  class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="claude-sonnet-4">Claude Sonnet 4</option>
                  <option value="gpt-5">GPT-5</option>
                  <option value="claude-haiku">Claude Haiku</option>
                  <option value="gpt-4-turbo">GPT-4 Turbo</option>
                </select>
              </div>

              <button 
                @click="createAgent"
                :disabled="!newAgentGoal || isCreatingAgent"
                class="w-full px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white rounded-md hover:from-emerald-600 hover:to-cyan-600 disabled:opacity-50 transition-all duration-200"
              >
                {{ isCreatingAgent ? 'Creating Agent...' : 'Create Agent' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Agent Execution -->
      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <h2 class="text-xl font-semibold text-white mb-4">Execute Agent Goal</h2>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Agent Selection -->
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Select Agent</label>
            <select 
              v-model="selectedAgentId"
              class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">Choose an agent...</option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>
          </div>

          <!-- Goal Input -->
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Goal</label>
            <input 
              v-model="executionGoal"
              type="text"
              class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="What should the agent accomplish?"
            />
          </div>
        </div>

        <div class="mt-4">
          <button 
            @click="executeGoal"
            :disabled="!selectedAgentId || !executionGoal || isExecuting"
            class="px-6 py-2 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-md hover:from-emerald-600 hover:to-green-600 disabled:opacity-50 transition-all duration-200"
          >
            {{ isExecuting ? 'Executing...' : 'Execute Goal' }}
          </button>
        </div>
      </div>

      <!-- Execution Results -->
      <div v-if="executionResult" class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <h2 class="text-xl font-semibold text-white mb-4">Execution Results</h2>
        
        <!-- Summary -->
        <div class="bg-slate-700/30 rounded-lg p-4 mb-6">
          <div class="flex items-center justify-between mb-2">
            <h3 class="font-medium text-white">Summary</h3>
            <div class="flex items-center space-x-4 text-sm text-slate-300">
              <span>Time: {{ formatTime(executionResult.executionTime) }}</span>
              <span>Cost: ${{ executionResult.cost.toFixed(4) }}</span>
              <span :class="executionResult.success ? 'text-emerald-400' : 'text-red-400'">
                {{ executionResult.success ? 'Success' : 'Failed' }}
              </span>
            </div>
          </div>
          <p class="text-slate-300">{{ executionResult.finalOutput.summary }}</p>
        </div>

        <!-- Execution Steps -->
        <div class="space-y-4">
          <h3 class="font-medium text-white">Execution Steps</h3>
          <div v-for="step in executionResult.steps" :key="step.stepNumber" class="border border-slate-600 rounded-lg p-4 bg-slate-700/20">
            <div class="flex items-start justify-between">
              <div class="flex items-center">
                <div class="w-8 h-8 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center text-sm font-medium mr-3">
                  {{ step.stepNumber }}
                </div>
                <div>
                  <p class="font-medium text-white">{{ step.description }}</p>
                  <p v-if="step.tool" class="text-sm text-slate-400">Using: {{ step.tool }}</p>
                </div>
              </div>
              <div class="flex items-center space-x-2">
                <span class="text-sm text-slate-400">{{ formatTime(step.executionTime) }}</span>
                <span :class="step.success ? 'text-emerald-400' : 'text-red-400'">
                  {{ step.success ? '✅' : '❌' }}
                </span>
              </div>
            </div>
            
            <div v-if="step.result" class="mt-3 p-3 bg-slate-600/30 rounded text-sm">
              <p class="font-medium text-slate-300 mb-1">Result:</p>
              <pre class="text-slate-400 whitespace-pre-wrap">{{ JSON.stringify(step.result, null, 2) }}</pre>
            </div>
            
            <div v-if="step.error" class="mt-3 p-3 bg-red-500/20 rounded text-sm">
              <p class="font-medium text-red-300 mb-1">Error:</p>
              <p class="text-red-400">{{ step.error }}</p>
            </div>
          </div>
        </div>

        <!-- Recommendations -->
        <div v-if="executionResult.finalOutput.recommendations" class="mt-6">
          <h3 class="font-medium text-white mb-2">Recommendations</h3>
          <ul class="list-disc list-inside space-y-1 text-slate-300">
            <li v-for="recommendation in executionResult.finalOutput.recommendations" :key="recommendation">
              {{ recommendation }}
            </li>
          </ul>
        </div>
      </div>

      <!-- Agent List -->
      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <h2 class="text-xl font-semibold text-white mb-4">Your Agents</h2>
        
        <div v-if="agents.length === 0" class="text-center py-8">
          <p class="text-slate-400">No agents created yet. Create your first agent above.</p>
        </div>
        
        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div 
            v-for="agent in agents" 
            :key="agent.id"
            class="border border-slate-600 rounded-lg p-4 hover:bg-slate-700/30 transition-all duration-200 bg-slate-700/20"
          >
            <div class="flex items-start justify-between mb-3">
              <h3 class="font-medium text-white">{{ agent.name }}</h3>
              <span :class="[
                'px-2 py-1 text-xs rounded-full',
                agent.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/50 text-slate-300'
              ]">
                {{ agent.status }}
              </span>
            </div>
            
            <p class="text-sm text-slate-300 mb-3">{{ agent.description }}</p>
            
            <div class="space-y-2">
              <div class="flex justify-between text-sm">
                <span class="text-slate-400">Accuracy:</span>
                <span class="font-medium text-white">{{ agent.performance.accuracy }}%</span>
              </div>
              <div class="flex justify-between text-sm">
                <span class="text-slate-400">Response Time:</span>
                <span class="font-medium text-white">{{ agent.performance.responseTime }}s</span>
              </div>
              <div class="flex justify-between text-sm">
                <span class="text-slate-400">Tasks Completed:</span>
                <span class="font-medium text-white">{{ agent.performance.tasksCompleted }}</span>
              </div>
            </div>
            
            <div class="mt-4 pt-3 border-t border-slate-600/50">
              <div class="flex flex-wrap gap-1">
                <span 
                  v-for="tool in agent.tools.slice(0, 3)" 
                  :key="tool"
                  class="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded"
                >
                  {{ tool }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { OILFIELD_AGENTS, youtuAgentIntegration } from '../services/youtuAgentIntegration'
import AppLayout from '../components/layout/AppLayout.vue'

// Reactive data
const isCreatingAgent = ref(false)
const isExecuting = ref(false)
const selectedAgentType = ref<any>(null)
const selectedAgentId = ref('')
const selectedModel = ref('claude-sonnet-4')
const newAgentGoal = ref('')
const newAgentContext = ref('')
const executionGoal = ref('')
const agents = ref<any[]>([])
const executionResult = ref<any>(null)

// Initialize
onMounted(async () => {
  await loadAgents()
})

// Load agents
const loadAgents = async () => {
  try {
    agents.value = await youtuAgentIntegration.getAgents()
  } catch (error) {
    console.error('Error loading agents:', error)
  }
}



// Select agent type
const selectAgentType = (agentType: any) => {
  selectedAgentType.value = agentType
  newAgentGoal.value = `Use ${agentType.name} to ${agentType.description.toLowerCase()}`
}

// Create agent
const createAgent = async () => {
  if (!selectedAgentType.value || !newAgentGoal.value) return

  isCreatingAgent.value = true
  try {
    const config = {
      name: selectedAgentType.value.name,
      description: selectedAgentType.value.description,
      goal: newAgentGoal.value,
      tools: selectedAgentType.value.tools,
      model: selectedModel.value,
      systemPrompt: `You are ${selectedAgentType.value.name}. ${selectedAgentType.value.description}`
    }

    const agent = await youtuAgentIntegration.createAgent(config)
    agents.value.unshift(agent)
    
    // Reset form
    selectedAgentType.value = null
    newAgentGoal.value = ''
    newAgentContext.value = ''
    
    // Show success message
    alert('Agent created successfully!')
  } catch (error) {
    console.error('Error creating agent:', error)
    alert('Error creating agent. Please try again.')
  } finally {
    isCreatingAgent.value = false
  }
}

// Execute goal
const executeGoal = async () => {
  if (!selectedAgentId.value || !executionGoal.value) return

  isExecuting.value = true
  try {
    const result = await youtuAgentIntegration.executeGoal(
      selectedAgentId.value,
      executionGoal.value,
      { context: newAgentContext.value }
    )
    executionResult.value = result
    
    // Scroll to results
    setTimeout(() => {
      const resultsElement = document.querySelector('.bg-white.rounded-lg.shadow-sm.border.border-gray-200.p-6.mb-8')
      if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: 'smooth' })
      }
    }, 100)
  } catch (error) {
    console.error('Error executing goal:', error)
    alert('Error executing goal. Please try again.')
  } finally {
    isExecuting.value = false
  }
}

// Quick demo function
const quickDemo = async () => {
  // Create a demo agent first
  const demoConfig = {
    name: 'Safety Compliance Agent',
    description: 'Monitors and ensures safety compliance across all operations',
    goal: 'Conduct a comprehensive safety audit of all active job sites',
    tools: ['checkCompliance', 'generateReport', 'searchJobs'],
    model: 'claude-sonnet-4',
    systemPrompt: 'You are a Safety Compliance Agent. Monitor and ensure safety compliance across all operations.'
  }

  try {
    const agent = await youtuAgentIntegration.createAgent(demoConfig)
    agents.value.unshift(agent)
    
    // Execute the goal immediately
    const result = await youtuAgentIntegration.executeGoal(
      agent.id,
      'Conduct a comprehensive safety audit of all active job sites',
      { context: 'Demo execution for new user' }
    )
    executionResult.value = result
    
    // Scroll to results
    setTimeout(() => {
      const resultsElement = document.querySelector('.bg-white.rounded-lg.shadow-sm.border.border-gray-200.p-6.mb-8')
      if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: 'smooth' })
      }
    }, 100)
    
  } catch (error) {
    console.error('Error in quick demo:', error)
    alert('Demo completed! Check the execution results below.')
  }
}

// Utility functions
const formatTime = (ms: number) => {
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}
</script>
